import Axios from 'axios';
import { set_on_progress } from 'src/config/redux/slicer/main/menuBarSlicer';
import { store } from 'src/config/redux/store';

export const DEFAULT_COORDS = { lat: -6.2088, lng: 106.8456 }; // Jakarta default
export const DEFAULT_LOCATION = {
  city: 'Jakarta',
  province: 'DKI Jakarta',
  coords: DEFAULT_COORDS,
  address: [
    { long_name: 'Jakarta', short_name: 'Jakarta', types: ['administrative_area_level_2'] },
    { long_name: 'DKI Jakarta', short_name: 'DKI Jakarta', types: ['administrative_area_level_1'] },
  ],
  full_address: 'DKI Jakarta, Indonesia',
  label: '',
};

export const parseStoredLocation = (raw: string | null) => {
  if (!raw) return DEFAULT_LOCATION;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && parsed.coords && typeof parsed.coords.lat === 'number' && typeof parsed.coords.lng === 'number' && !isNaN(parsed.coords.lat) && !isNaN(parsed.coords.lng)) {
      return parsed;
    }
    if (typeof parsed?.coords === 'string' && parsed.coords.includes(',')) {
      const [lat, lng] = parsed.coords.split(',').map(Number);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { ...parsed, coords: { lat, lng } };
      }
    }
  } catch (e) {
    console.error('Error parsing user_location', e);
  }
  return DEFAULT_LOCATION;
};

export const USER_LOCAL_STORAGE_LOCATION = parseStoredLocation(
  typeof window !== 'undefined' ? localStorage.getItem('user_location') : null
);

export const getAddressLabel = (
  address_components: any[] = [],
  type: string,
  name_type: 'long_name' | 'short_name' = 'long_name'
) => {
  const filtered_label = (address_components || []).filter(
    (address) => address && Array.isArray(address.types) && address.types.includes(type)
  );
  if (filtered_label.length > 0) return filtered_label[0][name_type] || filtered_label[0].long_name || 'No data';
  return 'No data';
};

export const getAddressFromCoords = async (data: { lat: number; lng: number }) => {
  store.dispatch(set_on_progress(true));
  const lat = Number(data.lat);
  const lng = Number(data.lng);

  // Helper so geometry.location.lat works as both property and callable function
  const latFn: any = () => lat;
  latFn.valueOf = () => lat;
  const lngFn: any = () => lng;
  lngFn.valueOf = () => lng;

  // 1. Try ArcGIS Reverse Geocoding (reliable, detailed Indonesian streets, no API key, CORS enabled)
  try {
    const res = await Axios.get('https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/reverseGeocode', {
      params: {
        location: `${lng},${lat}`,
        f: 'json',
      },
      timeout: 4000,
    });

    const addr = res.data?.address;
    if (addr && (addr.Match_addr || addr.LongLabel || addr.Address)) {
      const route = addr.Address || addr.PlaceName || addr.Neighborhood || '';
      const city = addr.Subregion || addr.City || addr.MetroArea || 'Jakarta';
      const province = addr.Region || 'DKI Jakarta';
      const country = addr.CntryName || 'Indonesia';
      const full_address = addr.LongLabel || addr.Match_addr || `${route}, ${city}, ${province}`;
      const label = addr.PlaceName || route || city;

      return {
        address_components: [
          { long_name: route, short_name: route, types: ['route'] },
          { long_name: city, short_name: city, types: ['administrative_area_level_2'] },
          { long_name: province, short_name: province, types: ['administrative_area_level_1'] },
          { long_name: country, short_name: addr.CountryCode || 'ID', types: ['country'] },
        ],
        formatted_address: full_address,
        geometry: { location: { lat: latFn, lng: lngFn } },
        name: label,
        coords: { lat, lng },
      };
    }
  } catch (err) {
    // Fall through to backup
  }

  // 2. Backup: BigDataCloud free client reverse geocoding
  try {
    const res = await Axios.get('https://api.bigdatacloud.net/data/reverse-geocode-client', {
      params: {
        latitude: lat,
        longitude: lng,
        localityLanguage: 'id',
      },
      timeout: 3000,
    });
    const data = res.data;
    if (data) {
      const route = data.locality || '';
      const city = data.city || data.principalSubdivision || 'Jakarta';
      const province = data.principalSubdivision || 'DKI Jakarta';
      const country = data.countryName || 'Indonesia';
      const full_address = [route, city, province, country].filter(Boolean).join(', ');
      return {
        address_components: [
          { long_name: route, short_name: route, types: ['route'] },
          { long_name: city, short_name: city, types: ['administrative_area_level_2'] },
          { long_name: province, short_name: province, types: ['administrative_area_level_1'] },
          { long_name: country, short_name: data.countryCode || 'ID', types: ['country'] },
        ],
        formatted_address: full_address,
        geometry: { location: { lat: latFn, lng: lngFn } },
        name: route || city,
        coords: { lat, lng },
      };
    }
  } catch (err) {
    // Fall through to fallback
  }

  store.dispatch(set_on_progress(false));
  return {
    address_components: [
      { long_name: 'Lokasi Terpilih', short_name: 'Lokasi', types: ['route'] },
      { long_name: 'Indonesia', short_name: 'ID', types: ['country'] },
    ],
    formatted_address: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
    geometry: { location: { lat: latFn, lng: lngFn } },
    name: 'Lokasi Terpilih',
    coords: { lat, lng },
  };
};

export const resolveArcGisMagicKey = async (magicKey: string, fallbackText?: string) => {
  try {
    const res = await Axios.get('https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates', {
      params: {
        magicKey,
        f: 'json',
        maxLocations: 1,
        outFields: 'Match_addr,City,Subregion,Region,Country,Postal',
      },
      timeout: 3500,
    });
    const cand = res.data?.candidates?.[0];
    if (cand && cand.location) {
      const lat = cand.location.y;
      const lng = cand.location.x;
      const attr = cand.attributes || {};
      const full_address = cand.address || fallbackText || '';
      const name = full_address.split(',')[0].trim();
      const city = attr.Subregion || attr.City || name;
      const province = attr.Region || '';

      return {
        name,
        full_address,
        city,
        province,
        coords: { lat, lng },
        address_components: [
          { long_name: name, short_name: name, types: ['route'] },
          { long_name: city, short_name: city, types: ['administrative_area_level_2'] },
          { long_name: province, short_name: province, types: ['administrative_area_level_1'] },
          { long_name: countryCodeToName(attr.Country), short_name: 'ID', types: ['country'] },
        ],
      };
    }
  } catch (err) {
    console.warn('Error resolving magicKey', err);
  }
  return null;
};

const countryCodeToName = (code?: string) => {
  if (code === 'IDN' || code === 'ID') return 'Indonesia';
  return code || 'Indonesia';
};

export const searchAddress = async (query: string, currentCoords?: { lat: number; lng: number }) => {
  if (!query || query.trim().length < 2) return [];
  const results: any[] = [];
  const seen = new Set<string>();
  const trimmed = query.trim();

  // 1. Parallel fetch: Open-Meteo (cities) + ArcGIS Suggest (streets, landmarks, addresses in 300ms)
  const [meteoRes, esriRes] = await Promise.allSettled([
    Axios.get('https://geocoding-api.open-meteo.com/v1/search', {
      params: {
        name: trimmed,
        count: 5,
        language: 'id',
        format: 'json',
      },
      timeout: 3000,
    }),
    Axios.get('https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/suggest', {
      params: {
        text: trimmed,
        countryCode: 'IDN',
        maxSuggestions: 6,
        f: 'json',
      },
      timeout: 3000,
    }),
  ]);

  // Process Open-Meteo results (already has lat/lng)
  if (meteoRes.status === 'fulfilled' && meteoRes.value.data?.results) {
    for (const item of meteoRes.value.data.results) {
      const name = item.name;
      const city = item.admin2 || item.name;
      const province = item.admin1 || '';
      const country = item.country || 'Indonesia';
      const full_address = [name, city !== name ? city : '', province, country].filter(Boolean).join(', ');
      const key = `${item.latitude.toFixed(3)},${item.longitude.toFixed(3)}`;
      seen.add(name.toLowerCase());
      seen.add(key);
      results.push({
        name,
        full_address,
        city,
        province,
        coords: { lat: item.latitude, lng: item.longitude },
        address_components: [
          { long_name: name, short_name: name, types: ['route'] },
          { long_name: city, short_name: city, types: ['administrative_area_level_2'] },
          { long_name: province, short_name: province, types: ['administrative_area_level_1'] },
          { long_name: country, short_name: item.country_code || 'ID', types: ['country'] },
        ],
      });
    }
  }

  // Process ArcGIS suggestions (instant suggestions for addresses, streets, POIs)
  if (esriRes.status === 'fulfilled' && esriRes.value.data?.suggestions) {
    for (const item of esriRes.value.data.suggestions) {
      const text = item.text || '';
      const name = text.split(',')[0].trim();
      if (!seen.has(name.toLowerCase()) && !seen.has(text.toLowerCase())) {
        seen.add(name.toLowerCase());
        seen.add(text.toLowerCase());
        results.push({
          name,
          full_address: text,
          city: '',
          province: '',
          magicKey: item.magicKey,
        });
      }
    }
  }

  // 3. Fallback: if no suggestions found from both, try findAddressCandidates directly
  if (results.length === 0) {
    try {
      const candRes = await Axios.get('https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates', {
        params: {
          SingleLine: trimmed,
          countryCode: 'IDN',
          f: 'json',
          maxLocations: 5,
          outFields: 'Match_addr,City,Subregion,Region,Country',
        },
        timeout: 4000,
      });

      for (const cand of candRes.data?.candidates || []) {
        const loc = cand.location;
        const attr = cand.attributes || {};
        const key = `${loc.y.toFixed(3)},${loc.x.toFixed(3)}`;
        if (!seen.has(key)) {
          seen.add(key);
          const name = cand.address ? cand.address.split(',')[0].trim() : trimmed;
          const city = attr.Subregion || attr.City || name;
          const province = attr.Region || '';
          results.push({
            name,
            full_address: cand.address,
            city,
            province,
            coords: { lat: loc.y, lng: loc.x },
            address_components: [
              { long_name: name, short_name: name, types: ['route'] },
              { long_name: city, short_name: city, types: ['administrative_area_level_2'] },
              { long_name: province, short_name: province, types: ['administrative_area_level_1'] },
              { long_name: 'Indonesia', short_name: 'ID', types: ['country'] },
            ],
          });
        }
      }
    } catch (e) {
      // ignore
    }
  }

  return results;
};

// Backward compatibility alias
export const searchAddressPhoton = searchAddress;
